import React from "react";
import { PROMOTION_COPY } from "../brand/constants";

const DEFAULT_PROMOTION_SCORE = 5;
const DEFAULT_NUM_IDEAS = 3;
const MIN_NUM_IDEAS = 1;
const MAX_NUM_IDEAS = 10;

const clampPromotionScore = (value, fallback = DEFAULT_PROMOTION_SCORE) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return fallback;
  return Math.max(0, Math.min(10, Math.round(parsed)));
};

const clampNumIdeas = (value, fallback = DEFAULT_NUM_IDEAS) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return fallback;
  return Math.max(MIN_NUM_IDEAS, Math.min(MAX_NUM_IDEAS, Math.round(parsed)));
};

/**
 * Popover for idea generation settings: number of ideas, promotion score, and optional prompt.
 * position: "up" = popover above trigger, bottom of popover aligns with bottom of button.
 * position: "down" = popover beside trigger (right), top of popover aligns with top of button.
 */
const IdeaGenerationSettingsPopover = ({
  promotionValue,
  onPromotionChange,
  promptValue,
  onPromptChange,
  numIdeas = DEFAULT_NUM_IDEAS,
  onNumIdeasChange,
  isOpen,
  onToggle,
  position = "down",
}) => {
  const displayValue = clampPromotionScore(promotionValue, DEFAULT_PROMOTION_SCORE);
  const displayNumIdeas = clampNumIdeas(numIdeas, DEFAULT_NUM_IDEAS);
  const promotionText = PROMOTION_COPY[displayValue] ?? PROMOTION_COPY[5];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        className={`h-9 w-9 inline-flex items-center justify-center rounded border ${
          isOpen ? "border-theme-primary text-theme-primary" : "border-theme-secondary text-theme-text"
        }`}
        aria-label="Idea generation settings"
        title="Idea generation settings"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M10.325 4.317a1.724 1.724 0 013.35 0 1.724 1.724 0 002.573 1.066 1.724 1.724 0 012.37 2.37 1.724 1.724 0 001.065 2.572 1.724 1.724 0 010 3.35 1.724 1.724 0 00-1.066 2.573 1.724 1.724 0 01-2.37 2.37 1.724 1.724 0 00-2.572 1.065 1.724 1.724 0 01-3.35 0 1.724 1.724 0 00-2.573-1.066 1.724 1.724 0 01-2.37-2.37 1.724 1.724 0 00-1.065-2.572 1.724 1.724 0 010-3.35 1.724 1.724 0 001.066-2.573 1.724 1.724 0 012.37-2.37 1.724 1.724 0 002.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>
      {isOpen ? (
        <>
          <div className="fixed inset-0 z-10" aria-hidden onClick={onToggle} />
          <div
            className={`absolute w-64 p-3 rounded-lg border border-theme-secondary bg-theme-main shadow-lg z-20 space-y-3 ${
              position === "up" ? "right-full mr-2 bottom-0" : "right-full mr-2 top-0"
            }`}
          >
          <div>
            <p className="text-xs font-semibold text-theme-text mb-2">Number of ideas</p>
            <input
              type="number"
              min={MIN_NUM_IDEAS}
              max={MAX_NUM_IDEAS}
              step={1}
              value={displayNumIdeas}
              onChange={(e) => onNumIdeasChange?.(clampNumIdeas(e.target.value, DEFAULT_NUM_IDEAS))}
              className="w-16 h-8 px-2 rounded border border-theme-secondary bg-theme-secondary text-theme-text text-sm"
            />
          </div>
          <div>
            <p className="text-xs font-semibold text-theme-text mb-2">Promotion score</p>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min={0}
                max={10}
                step={1}
                value={displayValue}
                onChange={(e) => onPromotionChange(clampPromotionScore(e.target.value, DEFAULT_PROMOTION_SCORE))}
                className="w-full accent-theme-primary"
              />
              <input
                type="number"
                min={0}
                max={10}
                step={1}
                value={displayValue}
                onChange={(e) => onPromotionChange(clampPromotionScore(e.target.value, DEFAULT_PROMOTION_SCORE))}
                className="w-12 h-8 px-1 rounded border border-theme-secondary bg-theme-secondary text-theme-text text-xs"
              />
            </div>
            <p
              className="text-[11px] text-theme-text opacity-80 mt-1 whitespace-nowrap overflow-hidden text-ellipsis"
              title={`${displayValue}/10 - ${promotionText}`}
            >
              {displayValue}/10 - {promotionText}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold text-theme-text mb-2">Optional prompt</p>
            <textarea
              value={promptValue || ""}
              onChange={(e) => onPromptChange(e.target.value)}
              placeholder="Guide idea generation (focus on X, avoid Y, etc)"
              rows={2}
              className="w-full px-2 py-1.5 rounded border border-theme-secondary bg-theme-secondary text-theme-text text-xs placeholder-theme-text placeholder-opacity-50 resize-none"
            />
          </div>
        </div>
        </>
      ) : null}
    </div>
  );
};

export default IdeaGenerationSettingsPopover;
